import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericTable from "../../../../Components/Tables/GenericTable";
import { PlusOutlined } from "@ant-design/icons";
import "../style.css";
import { useDateFilter } from "../../../../Contexts/DateFilterContext";
import { getVendorTransactionAdjustments } from "../../../../API/FuelVendor/TransactionAdjustments";

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const formatLabel = (str) =>
  str
    ? str
        .split("_")
        .map((word) => capitalize(word))
        .join(" ")
    : str;

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "-";
  return d
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, "-");
};

const formatTime = (isoDate) => {
  if (!isoDate) return "-";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const TransactionAdjustments = ({ setBtnConfigs, setExportData }) => {
  const navigate = useNavigate();
  const [transactionData, setTransactionData] = useState([]);
  const { from, to } = useDateFilter();

  const handleRequest = () => {
    navigate("/request-adjustments");
  };

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Request Adjustments",
        icon: <PlusOutlined />,
        onClick: handleRequest,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBtnConfigs]);

  const fetchAdjustments = async () => {
    try {
      const startDate = from ? from.split(" ")[0] : undefined;
      const endDate = to ? to.split(" ")[0] : undefined;

      const res = await getVendorTransactionAdjustments(startDate, endDate);

      if (Math.floor(res?.status / 100) === 2) {
        const items = res?.data?.data || [];

        setTransactionData(
          items.map((item) => ({
            key: String(item.id),
            fleetManager: item.fleet_company_name || "-",
            date: formatDate(item.created_at),
            time: formatTime(item.created_at),
            txnId: item.txn_id || "-",
            adjId: item.adj_id || "-",
            vehicleId: item.vehicle_id || "-",
            type: formatLabel(item.type) || "-",
            netDiff: parseFloat(item.net_diff) || 0,
            originalAmount: formatCurrency(item.original_amount),
            status: capitalize(item.status) || "-",
          })),
        );
      }
    } catch (err) {
      console.error("Failed to load transaction adjustments:", err);
    }
  };

  useEffect(() => {
    fetchAdjustments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => {
    setExportData(transactionData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionData]);

  const uniqueTxnTypes = [...new Set(transactionData.map((item) => item.type))];
  const txnTypeFilters = uniqueTxnTypes.map((t) => ({ text: t, value: t }));

  const statusFilters = [
    ...new Set(transactionData.map((item) => item.status)),
  ].map((status) => ({ text: status, value: status }));

  const fleetManagerFilters = [
    ...new Set(transactionData.map((item) => item.fleetManager)),
  ].map((name) => ({ text: name, value: name }));

  const transactionColumns = [
    {
      title: "Fleet Manager",
      dataIndex: "fleetManager",
      key: "fleetManager",
      enableSearch: true,
      filters: fleetManagerFilters,
      onFilter: (value, record) => record.fleetManager === value,
    },
    {
      title: "Date/Time",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            lineHeight: "1.2",
          }}
        >
          <span>{text}</span>
          <span style={{ color: "#71717A" }}>{record.time}</span>
        </div>
      ),
    },
    {
      title: "Transaction ID",
      dataIndex: "txnId",
      key: "txnId",
      enableSearch: true,
    },
    {
      title: "Adjustment ID",
      dataIndex: "adjId",
      key: "adjId",
      enableSearch: true,
    },
    {
      title: "Vehicle ID",
      dataIndex: "vehicleId",
      key: "vehicleId",
      enableSearch: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filters: txnTypeFilters,
      onFilter: (value, record) => record.type === value,
      render: (type) => (
        <span
          className={`type-badge badge-${type?.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {type}
        </span>
      ),
    },
    {
      title: "Net Diff",
      dataIndex: "netDiff",
      key: "netDiff",
      sorter: (a, b) => (a.netDiff || 0) - (b.netDiff || 0),
      render: (val) => {
        const isNegative = val < 0;
        return (
          <span
            style={{
              color: isNegative ? "#ef4444" : "#22c55e",
              fontWeight: "500",
            }}
          >
            {isNegative ? "-" : "+"}
            {formatCurrency(Math.abs(val))}
          </span>
        );
      },
    },
    {
      title: "Original Amount",
      dataIndex: "originalAmount",
      key: "originalAmount",
      sorter: (a, b) =>
        (parseFloat(a.originalAmount.replace(/[^0-9.-]+/g, "")) || 0) -
        (parseFloat(b.originalAmount.replace(/[^0-9.-]+/g, "")) || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      // Approve/Deny decisions are Admin-only; vendors see the status only.
      render: (status) => (
        <span className={`status-badge ${status?.toLowerCase() || ""}`}>
          {status || "-"}
        </span>
      ),
    },
  ];

  return (
    <div className="fv-transactions">
      <GenericTable
        dataSource={transactionData}
        columns={transactionColumns}
        enableRowSelection={false}
        onRowClick={(r) =>
          navigate("/transaction-adjustments-detail", { state: { id: r.key } })
        }
        onSelectionChange={(keys, rows) => console.log(keys, rows)}
      />
    </div>
  );
};

export default TransactionAdjustments;
